import { requestMutation } from '../lib/fasterdom/fasterdom';

type SafeAreaValues = {
  safeTop: number;
  safeBottom: number;
};

const CSS_SAFE_TOP = '--safe-top';
const CSS_SAFE_BOTTOM = '--safe-bottom';
const SAFE_AREA_TOP_OFFSET_CLASS = 'safe-area-extra-top-inset';
const SAFE_AREA_DEBUG_KEY = 'safe_area_debug';
const SAFE_AREA_COMPATIBLE_KEY = 'ssg_safe_area_compatible';
const SAFE_AREA_COMPATIBLE_QUERY_KEYS = [
  'ssgSafeAreaCompatible',
  'safeAreaCompatible',
  'dynamicIslandCompatible',
];

let isInitialized = false;
let measureElement: HTMLDivElement | undefined;

function ensureMeasureElement() {
  if (measureElement) {
    return measureElement;
  }

  const element = document.createElement('div');
  element.style.position = 'fixed';
  element.style.top = '0';
  element.style.left = '0';
  element.style.width = '0';
  element.style.height = '0';
  element.style.visibility = 'hidden';
  element.style.pointerEvents = 'none';
  element.style.paddingTop = 'env(safe-area-inset-top, 0px)';
  element.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
  document.body.appendChild(element);
  measureElement = element;
  return element;
}

function readCssEnvInsets() {
  const element = ensureMeasureElement();
  const styles = window.getComputedStyle(element);
  const top = Number.parseFloat(styles.paddingTop) || 0;
  const bottom = Number.parseFloat(styles.paddingBottom) || 0;

  return { top, bottom };
}

function isIPhone() {
  const userAgent = navigator.userAgent || '';
  return /iPhone/i.test(userAgent);
}

function isIPhoneWithNotch() {
  const screenHeight = Math.max(window.screen.height || 0, window.screen.width || 0);
  const innerHeight = Math.max(window.innerHeight || 0, window.innerWidth || 0);
  return screenHeight >= 812 || screenHeight - innerHeight > 20;
}

function parseBooleanFlag(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
    return false;
  }

  return undefined;
}

function readCompatibilityFlagFromWindow() {
  const w = window as any;
  const candidates = [
    w.__SSG_SAFE_AREA_COMPATIBLE__,
    w.__SAFE_AREA_COMPATIBLE__,
    w.__DYNAMIC_ISLAND_COMPATIBLE__,
  ];

  for (const value of candidates) {
    const parsed = parseBooleanFlag(value);
    if (parsed !== undefined) {
      return parsed;
    }
  }

  return undefined;
}

function readCompatibilityFlagFromQuery() {
  try {
    const query = new URLSearchParams(window.location.search);
    for (const key of SAFE_AREA_COMPATIBLE_QUERY_KEYS) {
      const value = query.get(key);
      const parsed = parseBooleanFlag(value);
      if (parsed !== undefined) {
        return parsed;
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function readCompatibilityFlagFromStorage() {
  try {
    const sessionValue = window.sessionStorage.getItem(SAFE_AREA_COMPATIBLE_KEY);
    const parsedSession = parseBooleanFlag(sessionValue);
    if (parsedSession !== undefined) {
      return parsedSession;
    }

    const localValue = window.localStorage.getItem(SAFE_AREA_COMPATIBLE_KEY);
    return parseBooleanFlag(localValue);
  } catch {
    return undefined;
  }
}

function isWrapperSafeAreaCompatible() {
  const queryFlag = readCompatibilityFlagFromQuery();
  if (queryFlag !== undefined) {
    return queryFlag;
  }

  const windowFlag = readCompatibilityFlagFromWindow();
  if (windowFlag !== undefined) {
    return windowFlag;
  }

  return readCompatibilityFlagFromStorage();
}

function getFallbackSafeArea(): SafeAreaValues {
  if (!isIPhone()) {
    return {
      safeTop: 0,
      safeBottom: 0,
    };
  }

  if (isIPhoneWithNotch()) {
    return {
      safeTop: 44,
      safeBottom: 34,
    };
  }

  return {
    safeTop: 20,
    safeBottom: 0,
  };
}

function getSafeAreaValues(): SafeAreaValues {
  const isCompatible = isWrapperSafeAreaCompatible();
  if (isCompatible === true) {
    return {
      safeTop: 0,
      safeBottom: 0,
    };
  }

  const envInsets = readCssEnvInsets();

  if (envInsets.top > 0 || envInsets.bottom > 0) {
    return {
      safeTop: envInsets.top,
      safeBottom: envInsets.bottom,
    };
  }

  return getFallbackSafeArea();
}

function setCssVariables({ safeTop, safeBottom }: SafeAreaValues) {
  requestMutation(() => {
    document.documentElement.style.setProperty(CSS_SAFE_TOP, `${safeTop}px`);
    document.documentElement.style.setProperty(CSS_SAFE_BOTTOM, `${safeBottom}px`);
  });
}

function shouldDebug() {
  try {
    return Boolean((window as any).__SAFE_AREA_DEBUG__) || window.localStorage.getItem(SAFE_AREA_DEBUG_KEY) === '1';
  } catch {
    return Boolean((window as any).__SAFE_AREA_DEBUG__);
  }
}

function applySafeArea() {
  const values = getSafeAreaValues();
  setCssVariables(values);

  const shouldApplyTopOffset = values.safeTop > 0;
  requestMutation(() => {
    document.documentElement.classList.toggle(SAFE_AREA_TOP_OFFSET_CLASS, shouldApplyTopOffset);
  });

  if (shouldDebug()) {
    console.log('[safe-area]', {
      safeTop: values.safeTop,
      safeBottom: values.safeBottom,
      shouldApplyTopOffset,
      userAgent: navigator.userAgent,
    });
  }
}

export function initSafeArea() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applySafeArea, { once: true });
  } else {
    applySafeArea();
  }

  if (isInitialized) {
    return;
  }

  isInitialized = true;
  window.addEventListener('resize', applySafeArea);
  window.addEventListener('orientationchange', applySafeArea);
}
